import { useCallback } from 'react';
import {
	Heading,
	LoadingButton,
	FieldForSubscription,
	RouterLink,
	Stack,
	Box,
	TextField,
	Form,
	Field,
} from 'components/common';
import { useLogin } from 'core/uses-cases/authentication';
import { router } from 'navigation';
import { useTranslation } from 'hooks';
import * as Yup from 'yup';

type LoginFormSubmittedValues = {
	username: string;
	password: string;
};

type LoginFormProps = {
	initialValues?: {
		username: string;
	};
};

export const LoginForm = ({ initialValues }: LoginFormProps) => {
	const { doLoginAsync } = useLogin();
	const { t } = useTranslation();

	const handleOnSubmit = useCallback(
		(values: LoginFormSubmittedValues) =>
			doLoginAsync(values).catch(() => null),
		[]
	);

	const validationSchema = Yup.object().shape({
		username: Yup.string()
			.strict(false)
			.trim()
			.email('Please enter a valid email address')
			.required('Please enter an email address'),
		password: Yup.string().required('Please enter a password'),
	});

	return (
		<Form
			onSubmit={handleOnSubmit}
			validationSchema={validationSchema}
			subscription={{ submitting: true }}
			initialValues={initialValues}
		>
			{({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Stack spacing={3} alignItems='center'>
						<Heading variant='h6'>{t('login.log-in')}</Heading>
						<Box alignSelf='stretch'>
							<Field
								name='username'
								component={TextField}
								type='text'
								placeholder={t('common-label.email-address')}
								fullWidth
							/>
						</Box>
						<Box alignSelf='stretch'>
							<Field
								name='password'
								component={TextField}
								type='password'
								placeholder={t('common-label.password')}
								fullWidth
							/>
						</Box>
						<LoadingButton
							sx={{ alignSelf: 'center' }}
							loading={submitting}
							loadingIndicator={t('login.logging-in')}
							size='small'
							type='submit'
						>
							{t('login.log-in')}
						</LoadingButton>
						<FieldForSubscription
							name='username'
							subscription={{
								value: true,
								valid: true,
							}}
						>
							{props => (
								<RouterLink
									to={router.getPathFor(
										'account.reset',
										{},
										{
											email: props.meta.valid
												? props.input.value
												: '',
										}
									)}
								>
									{t('login.forgot-your-password')}
								</RouterLink>
							)}
						</FieldForSubscription>
					</Stack>
				</form>
			)}
		</Form>
	);
};
